import React, { useCallback, useMemo, useState } from 'react';
import { TouchableOpacity, View, StyleSheet } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import {
  IResidentUserState,
  RESIDENT_NAVIGATION_ITEMS,
  NAVIGATION_GROUP_LABELS,
  NAVIGATION_GROUP_ORDER,
} from '@/constants/residents/residentNavigation';
import {
  ISharedNavigationItem,
  ISharedNavigationGroupConfig,
  SvgComponent,
} from '@/constants/shared/navigation';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import {
  MobileHeaderWithGlassMenu,
  MobileHeaderVariant,
} from '@/components/shared/redesign';
import { useAppSelector } from '@/store';
import { MobileNotificationsModal } from '@/components/residents/notification/mobile/MobileNotificationsModal';
import { useUnreadNotificationsCount } from '@/hooks/common/useUnreadNotificationsCount';
import { logger } from '@/utils/common/logger';

interface ResidentMobileHeaderWithMenuProps {
  onLogoPress?: () => void;
  onSignOut?: () => void;
  onNavigate?: (path: string) => void;
  onNotificationPress?: () => void;
  currentPath?: string;
  currentTitle?: string;
  showNotification?: boolean;
  variant?: MobileHeaderVariant | 'default';
}

interface ResidentNotificationBellProps {
  variant: MobileHeaderVariant;
  unreadCount: number;
  onPress: () => void;
}

const ResidentNotificationBell: React.FC<ResidentNotificationBellProps> = ({
  variant,
  unreadCount,
  onPress,
}) => {
  const { colors } = useThemeColors();
  const buttonStyle =
    variant === 'image'
      ? { backgroundColor: colors.glass.buttonBgImage, borderColor: colors.glass.buttonBorderImage }
      : { backgroundColor: colors.glass.buttonBg, borderColor: colors.glass.buttonBorder };

  return (
    <TouchableOpacity
      style={[bellStyles.button, buttonStyle]}
      onPress={onPress}
      accessibilityLabel="Notifications"
      accessibilityRole="button"
    >
      <RESIDENTS_SVG_COMPONENTS.BELL width={20} height={20} color={colors.text.secondary} />
      {unreadCount > 0 && (
        <View
          style={[
            bellStyles.unreadDot,
            { backgroundColor: colors.accent.red, borderColor: colors.bg.base },
          ]}
        />
      )}
    </TouchableOpacity>
  );
};

export const ResidentMobileHeaderWithMenu: React.FC<ResidentMobileHeaderWithMenuProps> = ({
  onLogoPress,
  onSignOut,
  onNavigate,
  onNotificationPress,
  currentPath = '/payments',
  showNotification = true,
  variant = 'gradient',
}) => {
  const navigation = useNavigation<any>();
  const { user } = useAppSelector((state) => state.auth);
  const { total: unreadCount } = useUnreadNotificationsCount();
  const [isNotificationModalVisible, setIsNotificationModalVisible] = useState(false);

  // 'default' is being retired in favor of the redesign — render gradient instead.
  const resolvedVariant: MobileHeaderVariant = variant === 'image' ? 'image' : 'gradient';

  const userState: IResidentUserState = useMemo(
    () => ({
      customerType: user?.customerType,
      isWifiEnabled: false,
    }),
    [user?.customerType],
  );

  const navItems = useMemo<ISharedNavigationItem[]>(
    () =>
      RESIDENT_NAVIGATION_ITEMS.filter(
        (item) => !item.condition || item.condition(userState),
      ).map((item) => ({
        id: item.id,
        title: item.title,
        path: item.path,
        group: item.group,
        icon: (RESIDENTS_SVG_COMPONENTS[item.iconKey] as SvgComponent | undefined) ?? null,
      })),
    [userState],
  );

  const navGroupConfig = useMemo<ISharedNavigationGroupConfig>(
    () => ({
      order: [...NAVIGATION_GROUP_ORDER],
      labels: { ...NAVIGATION_GROUP_LABELS },
    }),
    [],
  );

  const handleNotificationPress = useCallback(() => {
    setIsNotificationModalVisible(true);
  }, []);

  const handleSeeAllNotifications = useCallback(() => {
    setIsNotificationModalVisible(false);
    onNotificationPress?.();
  }, [onNotificationPress]);

  const handleCloseNotificationModal = useCallback(() => {
    setIsNotificationModalVisible(false);
  }, []);

  const handleNotificationSettings = useCallback(() => {
    setIsNotificationModalVisible(false);
    try {
      navigation.navigate('MobileAccountSettings', { section: 'notificationSettings' });
    } catch (error) {
      console.warn('Failed to navigate to notification settings:', error);
    }
  }, [navigation]);

  const handlePropertyChatPress = useCallback(() => {
    setIsNotificationModalVisible(false);
    try {
      navigation.navigate('ResidentMessages');
    } catch (e) {
      logger.warn('headerMenuActionFailed', e);
    }
  }, [navigation]);

  const handleNavigate = useCallback(
    (path: string) => {
      onNavigate?.(path);
    },
    [onNavigate],
  );

  const rightHeaderSlot = showNotification ? (
    <ResidentNotificationBell
      variant={resolvedVariant}
      unreadCount={unreadCount}
      onPress={handleNotificationPress}
    />
  ) : null;

  return (
    <>
      <MobileHeaderWithGlassMenu
        currentPath={currentPath}
        onNavigate={handleNavigate}
        onLogoPress={onLogoPress}
        onSignOut={onSignOut}
        navItems={navItems}
        navGroupConfig={navGroupConfig}
        rightHeaderSlot={rightHeaderSlot}
        variant={resolvedVariant}
      />

      <MobileNotificationsModal
        visible={isNotificationModalVisible}
        onClose={handleCloseNotificationModal}
        onSeeAll={handleSeeAllNotifications}
        onSettings={handleNotificationSettings}
        onPropertyChatPress={handlePropertyChatPress}
      />
    </>
  );
};

const bellStyles = StyleSheet.create({
  button: {
    width: 40,
    height: 40,
    borderWidth: 1,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  unreadDot: {
    position: 'absolute',
    top: 8,
    right: 8,
    width: 8,
    height: 8,
    borderRadius: 4,
    borderWidth: 2,
  },
});
